import { Button, Col, Row } from 'antd';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { getSales } from '../../../../appRedux/actions';
import {
  filters,
  paymentStatusDictionary,
  posSearchFilters,
  saleTypeConst,
  searchFilters,
  searchInputInitialValues,
  salesConst,
  treatmentStatusDictionary,
} from '../../../../constants/Sales';
import { useCustomModal } from '../../../../hooks/useCustomModal';
import { ListTable, ModalView, TableFilters } from '../../../uiComponents';
import { SalesModalBody } from '../SalesModalBody';
import { formatPrice } from '../../../../util/formatPrice';
import { FileTextOutlined } from '@ant-design/icons';
import { setPaymentStatus } from '../../../../util/orders';
import { SaleTableOptions } from '../SaleTableOptions';
import { SalesRefundModalBody } from '../SalesRefundModalBody';
import { getFullName } from '../../../../util';
import moment from 'moment';
import { Modal } from 'antd';
import { SantanderModalBody } from '../SantanderModalBody';
import { CustomerModal } from '../../Customer/CustomerModalBody';

export const SalesTable = ({ saleType, headerAction = null }) => {
  const isOnlineSale = saleType === saleTypeConst.ONLINE;
  const isPosSale = saleType === saleTypeConst.POS;
  const showOrderTracking = isOnlineSale || isPosSale;
  const availableSearchFilters = isPosSale ? posSearchFilters : searchFilters;
  const {
    privileges: { branch: privileges = [] },
  } = useSelector((state) => state.userAuth);

  const tableFilters = { ...filters };

  if (isOnlineSale) {
    delete tableFilters.branches;
    tableFilters.paymentGateway = true;
  } else if (isPosSale) {
    delete tableFilters.paymentStatus;
  }

  const formatPaymentGateway = (paymentGateway) => paymentGateway || '—';

  const { modal } = useCustomModal();
  const [title, setTitle] = useState({});
  const [width, setWidth] = useState(1150);
  const [children, setChildren] = useState(null);
  const [isSantaderModalOpen, setIsSantaderModalOpen] = useState(false);

  const getModal = (order, action) => {
    switch (action) {
      case 'customer':
        setTitle(getFullName(order.customer));
        setChildren(<CustomerModal data={order.customer} />);
        setWidth(1500);
        break;
      case 'refund':
        setTitle(`${salesConst.REFUND}: orden ${order.orderNumber}`);
        setChildren(<SalesRefundModalBody data={order} />);
        setWidth(850);
        break;
      default:
        setTitle(salesConst.DETAIL);
        setChildren(
          <SalesModalBody
            data={order}
            saleType={saleType}
            showOrderTracking={showOrderTracking}
          />
        );
        setWidth(showOrderTracking ? 1400 : 1150);
    }
    modal();
  };

  const handleOk = useCallback(() => {}, []);

  const handleCancel = useCallback(() => {
    setIsSantaderModalOpen(false);
  }, []);

  const handleOnClickSantander = useCallback(() => {
    setIsSantaderModalOpen(true);
  }, []);

  const columns = [
    {
      title: 'Orden',
      sorter: true,
      defaultSortOrder: 'descend',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (orderNumber, order) => (
        <Button
          type="link"
          htmlType="button"
          href="#/"
          onClick={() => {
            getModal(order);
          }}
        >
          {orderNumber}
        </Button>
      ),
    },
    {
      title: 'Folio de sucursal',
      dataIndex: 'folioTicket',
      key: 'folioTicket',
    },
    {
      title: 'Cliente',
      sorter: true,
      dataIndex: 'customer',
      key: 'customer',
      render: (customer) => <>{getFullName(customer)}</>,
      path: ['customer.name', 'customer.lastName', 'customer.motherLastName'],
    },
    {
      title: 'Método de pago',
      sorter: true,
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (paymentMethod) => paymentMethod || '-',
    },
    {
      title: 'Sucursal de venta',
      sorter: true,
      dataIndex: 'branch',
      key: 'branch',
      hidden: isOnlineSale,
      render: (branch) => branch?.name || '—',
      path: 'branch.name',
      formatter: (branch) => branch?.name || '—',
    },
    {
      title: 'Tipo de pasarela',
      dataIndex: 'paymentGateway',
      key: 'paymentGateway',
      hidden: !showOrderTracking,
      render: formatPaymentGateway,
      path: 'paymentGateway',
      formatter: formatPaymentGateway,
    },
    {
      title: 'Total',
      sorter: true,
      dataIndex: 'total',
      key: 'total',
      render: (total, order) =>
        total ? `${order.refundedAt ? '-' : ''}${formatPrice(total)}` : '-',
      path: 'default',
      formatter: (order) =>
        order.total
          ? `${order.refundedAt ? '-' : ''}${formatPrice(order.total)}`
          : '—',
    },
    {
      title: 'Estado de pago',
      sorter: true,
      dataIndex: 'paymentStatus',
      render: (status, order) =>
        isOnlineSale
          ? setPaymentStatus(status)
          : order.refundedAt
          ? 'Devolución'
          : 'Pagado',
      path: isOnlineSale ? 'paymentStatus' : 'default',
      formatter: isOnlineSale
        ? (status) => paymentStatusDictionary[status] || '—'
        : (order) => (order.refundedAt ? 'Devolución' : 'Pagado'),
    },
    {
      title: 'Estado del tratamiento',
      dataIndex: 'treatmentStatus',
      key: 'treatmentStatus',
      render: (status) => treatmentStatusDictionary[status] || '—',
      path: 'treatmentStatus',
      formatter: (status) => treatmentStatusDictionary[status] || '—',
    },
    {
      title: 'Sucursal responsable de canje',
      dataIndex: 'redemptionBranch',
      key: 'redemptionBranch',
      render: (branch) => branch || '—',
      path: 'redemptionBranch',
      formatter: (branch) => branch || '—',
    },
    {
      title: 'Fecha de adquisición',
      dataIndex: 'acquiredAt',
      key: 'acquiredAt',
      render: (date) => (date ? moment(date).format('DD/MM/YYYY') : '—'),
      path: 'acquiredAt',
      formatter: (date) => (date ? moment(date).format('DD/MM/YYYY') : '—'),
    },
    {
      title: 'Vigencia',
      dataIndex: 'validitySummary',
      key: 'validitySummary',
      render: (validity) =>
        validity && validity !== 'Varias'
          ? moment(validity).format('DD/MM/YYYY')
          : validity || '—',
      path: 'validitySummary',
      formatter: (validity) =>
        validity && validity !== 'Varias'
          ? moment(validity).format('DD/MM/YYYY')
          : validity || '—',
    },
    {
      title: 'Opciones',
      dataIndex: 'id',
      key: 'id',
      render: (id, order) => {
        if (saleType !== saleTypeConst.APP && !isOnlineSale) {
          return (
            <Button
              icon={<FileTextOutlined />}
              type="link"
              onClick={() => {
                order.refundedAt
                  ? window.open(
                      `${process.env.REACT_APP_API}/v${process.env.REACT_APP_API_VERSION}/order/${id}/ticket/refund`,
                      '_blank',
                      'noopener,noreferrer'
                    )
                  : window.open(
                      `${process.env.REACT_APP_API}/v${process.env.REACT_APP_API_VERSION}/order/${id}/ticket`,
                      '_blank',
                      'noopener,noreferrer'
                    );
              }}
            />
          );
        } else {
          return (
            <SaleTableOptions
              id={id}
              action={{
                viewCustomer: {
                  getModal,
                  params: [order, 'customer'],
                },
                refundOrder: {
                  getModal,
                  params: [order, 'refund'],
                },
              }}
              isRefund={order.refundedAt !== null}
              paymentStatus={order.paymentStatus}
              saleType={saleType}
            />
          );
        }
      },
    },
  ];

  return (
    <>
      {(headerAction || isOnlineSale || isPosSale) && (
        <Row
          align="middle"
          gutter={[12, 12]}
          style={{ marginBottom: '16px' }}
        >
          {headerAction && <Col>{headerAction}</Col>}
          {(isOnlineSale || isPosSale) && (
            <Col>
              <Button
                style={{
                  backgroundColor: '#fff',
                  color: '#ff4d4f',
                  borderColor: '#ff4d4f',
                }}
                onClick={handleOnClickSantander}
              >
                {isPosSale
                  ? salesConst.PAYMENT_LINKS
                  : salesConst.SANTANDER_ID}
              </Button>
            </Col>
          )}
        </Row>
      )}
      <Row>
        <Col span={24}>
          <TableFilters
            wrap
            justify="end"
            params={{ saleType }}
            privileges={privileges}
            searchFilters={availableSearchFilters}
            filters={tableFilters}
            searchInputInitialValues={searchInputInitialValues}
          />
        </Col>
      </Row>
      <ListTable
        columns={columns.filter((item) => !item.hidden)}
        getDataFunction={getSales}
        params={{ saleType }}
        privileges={privileges}
      />
      <ModalView width={width} title={title}>
        {children}
      </ModalView>
      <Modal
        title={
          isPosSale
            ? salesConst.PAYMENT_LINKS_MODAL_TITLE
            : salesConst.SANTANDER_MODAL_TITLE
        }
        open={isSantaderModalOpen}
        destroyOnClose
        onOk={handleOk}
        onCancel={handleCancel}
        width={600}
      >
        <SantanderModalBody
          isOpen={isSantaderModalOpen}
          referenceOnly={isPosSale}
        />
      </Modal>
    </>
  );
};
